import { useCallback, useEffect, useRef, useState } from 'react';

export type WakeLockStatus =
  | 'unsupported'
  | 'acquiring'
  | 'acquired'
  | 'released';

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener?(type: 'release', listener: () => void): void;
}

interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

function getApi(): WakeLockLike | null {
  if (typeof navigator === 'undefined') return null;
  const candidate = (navigator as unknown as { wakeLock?: WakeLockLike }).wakeLock;
  return candidate ?? null;
}

export interface UseWakeLockResult {
  status: WakeLockStatus;
  release(): void;
}

export function useWakeLock(active: boolean): UseWakeLockResult {
  const apiRef = useRef<WakeLockLike | null>(getApi());
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);
  const activeRef = useRef<boolean>(active);
  const manualReleasedRef = useRef<boolean>(false);
  const [status, setStatus] = useState<WakeLockStatus>(() => {
    const api = apiRef.current;
    if (!api) return 'unsupported';
    return active ? 'acquiring' : 'released';
  });

  const releaseInternal = useCallback(async (): Promise<void> => {
    const sentinel = sentinelRef.current;
    sentinelRef.current = null;
    if (!sentinel) {
      setStatus((prev) => (prev === 'unsupported' ? prev : 'released'));
      return;
    }
    try {
      await sentinel.release();
    } catch {
      /* swallow — release is best-effort */
    }
  }, []);

  useEffect(() => {
    activeRef.current = active;
    manualReleasedRef.current = false;
    const api = apiRef.current;

    if (!api) {
      setStatus('unsupported');
      return undefined;
    }

    if (!active) {
      void releaseInternal();
      setStatus((prev) => (prev === 'unsupported' ? prev : 'released'));
      return undefined;
    }

    let cancelled = false;
    setStatus('acquiring');

    const acquire = async (): Promise<void> => {
      try {
        const sentinel = await api.request('screen');
        if (cancelled || manualReleasedRef.current || !activeRef.current) {
          try {
            await sentinel.release();
          } catch {
            /* swallow */
          }
          return;
        }
        sentinelRef.current = sentinel;
        sentinel.addEventListener?.('release', () => {
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null;
            if (activeRef.current) {
              setStatus('acquiring');
            } else {
              setStatus('released');
            }
          }
        });
        setStatus('acquired');
      } catch {
        if (!cancelled) setStatus('released');
      }
    };

    void acquire();

    const onVisibilityChange = (): void => {
      if (!activeRef.current) return;
      if (sentinelRef.current) return;
      if (document.visibilityState === 'visible') {
        setStatus('acquiring');
        void acquire();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      void releaseInternal();
      setStatus((prev) => (prev === 'unsupported' ? prev : 'released'));
    };
  }, [active, releaseInternal]);

  const release = useCallback((): void => {
    manualReleasedRef.current = true;
    void releaseInternal();
  }, [releaseInternal]);

  return { status, release };
}
